import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, OnDestroy, RendererFactory2, ViewEncapsulation } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { MetaLinkDefinition } from '../objects/meta-link-definition';

@Injectable({
	providedIn: 'root'
})
export class HtmlLinkService implements OnDestroy {

	private routeListener: Subscription;

	constructor(
		private readonly router: Router,
		private rendererFactory: RendererFactory2,
		@Inject(DOCUMENT) document?: any) {
		this._document = document as Document;
	}
	private _document?: Document;
	/**
	 * Parse tag to create a selector
	 * @param  tag
	 * @return string selector to use in querySelector
	 */
	private _parseSelector(tag: MetaLinkDefinition) {
		const attr: string = tag.rel ? 'rel' : 'hreflang';
		return 'link[' + attr + '="' + tag[attr] + '"]';
	}

	/**
	 * Inject the State into the bottom of the <head>
	 */
	addTag(tag: MetaLinkDefinition, forceCreation?: boolean) {

		try {
			const renderer = this.rendererFactory.createRenderer(document, {
				id: '-1',
				encapsulation: ViewEncapsulation.None,
				styles: [],
				data: {}
			});

			const link = renderer.createElement('link');
			const head = document.head;
			const selector = this._parseSelector(tag);

			if (head === null) {
				throw new Error('tag <head> not found within DOCUMENT.');
			}

			Object.keys(tag).forEach((prop: string) => {
				return renderer.setAttribute(link, prop, tag[prop]);
			});

			// [TODO]: get them to update the existing one (if it exists) ?
			renderer.appendChild(head, link);

		} catch (e) {
			console.error('Error within linkService : ', e);
		}
	}

	/**
	 * Start listening on NavigationEnd router events
	 */
	startRouteListener() {
		this.routeListener = this.router.events.pipe(
			filter(event => event instanceof NavigationEnd)
		).subscribe(
			() => {
				let url = '';
				const urlTree = this.router.parseUrl(this.router.url);

				if (urlTree.root.hasChildren()) {

					const segments = urlTree.root.children['primary'].segments;

					if (segments && segments.length > 0) {
						url = segments.map(segment => segment.path).join('/');
					}
				}

				this.updateTag({
					rel: 'canonical',
					href: '/' + url
				});
			}
		);
	}

	/**
	 * Create or update a link tag
	 * @param  LinkDefinition tag
	 */
	updateTag(tag: MetaLinkDefinition) {
		const selector = this._parseSelector(tag);
		const linkElement = <HTMLLinkElement>document.head.querySelector(selector)
			|| document.head.appendChild(document.createElement('link'));

		if (linkElement) {
			Object.keys(tag).forEach((prop: string) => {
				linkElement[prop] = tag[prop];
			});
		}
	}

	/**
	 * Remove a link tag from DOM
	 * @param  tag
	 */
	removeTag(tag: MetaLinkDefinition) {
		const selector = this._parseSelector(tag);
		const linkElement = <HTMLLinkElement>document.head.querySelector(selector);

		if (linkElement) {
			document.head.removeChild(linkElement);
		}
	}

	/**
	 * Get link tag
	 * @param  tag
	 * @return HTMLLinkElement
	 */
	getTag(tag: MetaLinkDefinition) {
		const selector = this._parseSelector(tag);

		return document.head.querySelector(selector);
	}

	/**
	 * Get all link tags
	 * @return NodeListOf<HTMLLinkElement>
	 */
	getTags() {
		return document.head.querySelectorAll('link');
	}

	ngOnDestroy() {
		// this.routeListener.unsubscribe();
	}
}
